/**
 * Pagination — minimal prev / next / "more" row used by ChangelogView.
 *
 * Rendered as a single row of buttons + a current-count indicator. The
 * view owns the page state (`limit`); this component only emits intent
 * via callback and shows the visible count.
 *
 * The changelog endpoint caps at 200 rows, so this view simply lets the
 * caller request `200` once the user clicks "更多".
 */

import { el } from "../dom";

export interface PaginationOptions {
  /** Total rows currently rendered. */
  shown: number;
  /** Current cap; smaller of the cap and the actual server total. */
  cap: number;
  /** Max value the server will accept (defaults to 200). */
  max?: number;
  /** Callback when the user requests more rows. */
  onMore: () => void;
  /** Optional "刷新" (refresh) callback — omitted hides the button. */
  onRefresh?: () => void;
}

export function pagination({
  shown,
  cap,
  max = 200,
  onMore,
  onRefresh,
}: PaginationOptions): HTMLElement {
  const canMore = cap < max;
  const moreBtn = el(
    "button",
    {
      type: "button",
      class: "pagination-more",
      disabled: canMore ? null : "true",
      onclick: () => {
        if (canMore) onMore();
      },
    },
    canMore ? "更多" : "已是全部",
  );

  const refreshBtn = onRefresh
    ? el(
        "button",
        {
          type: "button",
          class: "pagination-refresh",
          onclick: () => onRefresh(),
        },
        "刷新",
      )
    : null;

  return el("div", { class: "pagination-row" }, [
    el("span", { class: "pagination-count" }, `显示 ${shown} 条 · 上限 ${cap}`),
    moreBtn,
    refreshBtn,
  ]);
}